import { Box, Button, TextField, Typography } from '@mui/material';
import React, { useState, useEffect, FormEvent } from 'react';
import { useSelector } from 'react-redux';
import { useHistory, useParams, NavLink as RouterLink } from 'react-router-dom';

import { CleanPattern } from 'app/model/cris/clean-patterns';
import { cleanPatternsService } from 'app/services/clean-patterns.service';
import { messagingService } from 'app/services/messaging.service';

import { selectProdDataStatus } from '../cris.slice';

interface Params {
    id?: string;
}

export const CleanPatternsForm: React.FC = () => {
    const { id } = useParams<Params>();
    const history = useHistory();
    const isNew = id === undefined;
    const showProd = useSelector(selectProdDataStatus);

    const [pattern, setPattern] = useState<Omit<CleanPattern, 'id'>>({
        source: '',
        version: '',
        substr: '',
        regex: '',
    });

    useEffect(() => {
        if (!isNew && id) {
            cleanPatternsService.get(id).subscribe({
                next: (data) => setPattern(data),
                error: () => messagingService.showError('Failed to load pattern'),
            });
        }
    }, [id, isNew]);

    const handleChange = (event: React.ChangeEvent<HTMLInputElement>) => {
        const { name, value } = event.target;
        setPattern((prev) => ({ ...prev, [name]: value }));
    };

    const handleSubmit = (evt: FormEvent) => {
        evt.preventDefault();

        if (showProd) {
            messagingService.showError('Cannot save in production mode');

            return;
        }
        if (!pattern.source.trim() || !pattern.version.trim() || !pattern.substr.trim()) {
            messagingService.showError('Source, Version & Substring are required');

            return;
        }

        const payload = isNew ? pattern : ({ ...pattern, id } as CleanPattern);

        cleanPatternsService.save(payload).subscribe({
            next: () => {
                messagingService.showSuccess(isNew ? 'Clean Pattern Created' : 'Clean Pattern Updated');
                history.push('/cris/clean-patterns');
            },
            error: () => messagingService.showError('Save failed'),
        });
    };

    return (
        <>
            <Typography variant='h6' gutterBottom>
                {isNew ? 'Create Clean Pattern' : `Edit Clean Pattern: ${id}`}
            </Typography>

            <form noValidate onSubmit={handleSubmit}>
                <Box display='flex' flexWrap='wrap' gap={2} mb={2}>
                    <TextField
                        name='source'
                        label='Source'
                        fullWidth
                        value={pattern.source}
                        onChange={handleChange}
                        disabled={showProd}
                    />
                    <TextField
                        name='version'
                        label='Version'
                        fullWidth
                        value={pattern.version}
                        onChange={handleChange}
                        disabled={showProd}
                    />
                    <TextField
                        name='substr'
                        label='Substring'
                        fullWidth
                        value={pattern.substr}
                        onChange={handleChange}
                        disabled={showProd}
                    />
                    <TextField
                        name='regex'
                        label='Regex'
                        fullWidth
                        value={pattern.regex}
                        onChange={handleChange}
                        disabled={showProd}
                    />
                </Box>

                <Box display='flex' gap={1}>
                    <Button type='submit' variant='outlined' color='primary'>
                        SAVE
                    </Button>
                    <Button variant='outlined' color='inherit' component={RouterLink} to='/cris/clean-patterns'>
                        CANCEL
                    </Button>
                </Box>
            </form>
        </>
    );
};
